import dayjs from 'dayjs';
import dayjsDuration from 'dayjs/plugin/duration';
import { useIntl } from '@doxyme/locale';
import { useElapsedTimer } from '@doxyme/ui/hooks';
import { useGetTime } from '../../../hooks/useGetTime';
import { useAppSelector } from '../../../hooks/useReduxToolkit';
import { getStartCallTime } from '../../../features/apolloSession/apolloSession.selectors';

dayjs.extend(dayjsDuration);

type Return = {
  parts: {
    /**
     * The value of the time part (e.g., number of hours, minutes, or seconds)
     */
    value: string | number;
    /**
     * The unit of the time part (e.g., "hours", "minutes", "seconds")
     */
    unit: string;
  }[];
};

export const useCallTimer = (): Return => {
  const getTime = useGetTime();
  const { formatMessage } = useIntl();

  const startCallTime = useAppSelector(getStartCallTime);

  const { elapsedMilliseconds } = useElapsedTimer({
    startTime: startCallTime ?? undefined,
    getCurrentTime: getTime,
  });

  const duration = dayjs.duration(elapsedMilliseconds);

  const hours = duration.hours();
  const minutes = duration.minutes();
  const seconds = duration.seconds().toString().padStart(2, '0');

  const parts = [];

  if (hours > 0) {
    parts.push({
      value: hours,
      unit: formatMessage({ id: 'call.duration.hoursUnit' }),
    });
  }

  if (minutes > 0) {
    parts.push({
      value: minutes,
      unit: formatMessage({ id: 'call.duration.minutesUnit' }),
    });
  }

  parts.push({
    value: seconds,
    unit: formatMessage({ id: 'call.duration.secondsUnit' }),
  });

  return { parts };
};
